import { ErrorModal } from "@dexteel/mesf-core";
import { Divider, Paper, Typography, makeStyles } from "@material-ui/core";
import moment, { Moment } from "moment";
import { useEffect, useState } from "react";
import { LazyBackDropLoading } from "../../../controls/LazyLoading";
import { DelaysParetoGraphic } from "./components/DelaysParetoGraphic";
import { Filters } from "./components/filters";
import { useSearchDelaysForPareto } from "./hooks/useSearchDelaysForPareto";
import { getDelayAreas } from "./repositories/DelaysParetoRepository";

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
		padding: "15px 20px 10px",
		borderRadius: "0.75rem",
		height: "91vh",
		display: "flex",
		flexDirection: "column",
		backgroundColor: "#f2f4f7",
	},
	header: {
		marginBottom: theme.spacing(2),
	},
	filterContainer: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-end",
		marginBottom: theme.spacing(2),
	},
	filterGroup: {
		display: "flex",
		alignItems: "flex-end",
	},
	buttonGroup: {
		display: "flex",
		gap: theme.spacing(2),
	},
	button: {
		whiteSpace: "nowrap",
	},
	divider: {
		margin: `${theme.spacing(2)}px 0`,
		width: "100%",
	},
	tableContainer: {
		width: "100%",
		height: "calc(100% - 20px)",
		padding: "2px",
		"& .ag-root-wrapper": {
			border: "none",
		},
		"& .ag-header": {
			borderTop: "1px solid #e0e0e0",
		},
	},
}));
type Props = {
	asset: "caster" | "eaf";
};
export const DelaysParetoPage = ({ asset }: Props) => {
	const classes = useStyles();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const assetId = asset === "caster" ? 13 : asset === "eaf" ? 14 : null;
	const assetName =
		asset === "caster" ? "Caster" : asset === "eaf" ? "EAF" : null;
	const [selectedDelayArea, setSelectedDelayArea] = useState<number | null>(
		assetId,
	);

	const initialEndDate = moment().endOf("day");
	const initialStartDate = initialEndDate
		.clone()
		.subtract(1, "month")
		.startOf("day");

	const [filterStartDate, setFilterStartDate] =
		useState<Moment>(initialStartDate);
	const [filterEndDate, setFilterEndDate] = useState<Moment>(initialEndDate);
	const [includeMicroDelays, setIncludeMicroDelays] = useState(false);
	const [includeUndeclared, setIncludeUndeclared] = useState(false);

	const [delayAreas, setDelayAreas] = useState<
		Array<{ AssetId: number; AssetName: string }>
	>([]);

	const searchDelaysForPareto = useSearchDelaysForPareto({
		setIsLoading,
		setError,
	});

	const fetchDelayAreas = async () => {
		const resp = await getDelayAreas();
		if (resp.ok && resp.data.tables.length > 0) {
			setDelayAreas(resp.data.tables[0].rows);
		} else {
			setError("An error occurred while fetching delay areas.");
		}
	};

	const handleFilterChange = (startDate: Moment, endDate: Moment) => {
		setFilterStartDate(startDate);
		setFilterEndDate(endDate);
		searchDelaysForPareto(
			startDate.toDate(),
			endDate.toDate(),
			selectedDelayArea,
		);
	};

	const handleDelayAreaChange = (newAssetId: number | null) => {
		setSelectedDelayArea(newAssetId);
		searchDelaysForPareto(
			filterStartDate.toDate(),
			filterEndDate.toDate(),
			newAssetId,
		);
	};

	const getFilteredDelayAreas = () => {
		if (asset === "eaf") {
			const filtered = delayAreas.filter(
				(area) => area.AssetName && area.AssetName.includes("EAF "),
			);
			if (assetId) return [{ AssetId: assetId, AssetName: "All" }, ...filtered];
		}
		return delayAreas;
	};

	useEffect(() => {
		fetchDelayAreas();
	}, []);

	useEffect(() => {
		setSelectedDelayArea(assetId);
	}, [assetId]);

	useEffect(() => {
		searchDelaysForPareto(
			filterStartDate.toDate(),
			filterEndDate.toDate(),
			selectedDelayArea,
			includeMicroDelays,
			includeUndeclared,
		);
	}, [
		asset,
		selectedDelayArea,
		filterStartDate,
		filterEndDate,
		includeMicroDelays,
		includeUndeclared,
	]);

	return (
		<Paper elevation={1} className={classes.root}>
			<LazyBackDropLoading open={isLoading} />
			<Typography
				variant="h5"
				className={classes.header}
				style={{ fontWeight: 600 }}
			>
				{assetName} Pareto Report
			</Typography>
			<div className={classes.filterContainer}>
				<div className={classes.filterGroup}>
					<Paper
						style={{
							display: "flex",
							alignItems: "center",
							height: "80px",
						}}
					>
						<Filters
							onFilterChange={handleFilterChange}
							initialStartDate={filterStartDate}
							initialEndDate={filterEndDate}
							includeMicroDelays={includeMicroDelays}
							includeUndeclared={includeUndeclared}
							onIncludeMicroDelaysChange={setIncludeMicroDelays}
							onIncludeUndeclaredChange={setIncludeUndeclared}
							delayAreas={asset === "eaf" ? getFilteredDelayAreas() : null}
							selectedDelayArea={selectedDelayArea}
							onDelayAreaChange={handleDelayAreaChange}
						/>
					</Paper>
				</div>
			</div>
			<DelaysParetoGraphic />
			<Divider style={{ marginBottom: 10 }} />
			<div className={classes.tableContainer}>
				<ErrorModal
					error={error}
					onHide={() => {
						setError("");
					}}
				/>
			</div>
		</Paper>
	);
};
